import { dehydrate } from '@tanstack/react-query';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { ProductDetail } from '@api/catalog';
import { getBrand } from '@api/catalog/brand/api';
import { getProductFilters, getProducts, getProductsAuth } from '@api/catalog/product/api';
import { getInStockFilter } from '@api/catalog/product/catalogSearchParams';
import { getPagination } from '@api/common/helpers';
import { FetchError } from '@api/common/types';
import { getSeoTags } from '@api/seo/api';

import { getSiteOrigin } from '@utils/config';
import { getCorrectPathname } from '@utils/getCorrectPathname';

import BrandInfo from '@components/BrandInfo';
import Picture from '@components/controls/future/Picture';
import { POPULAR_SORT_VALUE } from '@components/ControlViewItems/constants';
import { readStoredCatalogSort } from '@components/ControlViewItems/helpers';
import { SortFilter } from '@components/ControlViewItems/types';
import { useCatalogFilters } from '@components/Filters/ProductFilter/useCatalogFilters';
import JsonLdScript from '@components/JsonLdScript';
import PageSection from '@components/PageSection';
import PageWrapper from '@components/PageWrapper';
import ProductsList from '@components/ProductsList';
import { Breadcrumbs } from '@components/uiKit/Breadcrumbs';

import { HttpCode, ITEMS_PER_PRODUCTS_PAGE } from '@scripts/constants';
import { VIEW_OPTIONS } from '@scripts/enums';
import {
    declOfNum,
    fromRoubleToKopecks,
    getSeoFilter,
    getTotalPages,
    removeDefaultPaginationFromUrl,
    safeParseFilters,
    transformImageUrl,
} from '@scripts/helpers';
import { mergeProductsFilterResponses, splitLastFilter } from '@scripts/helpers/facets';
import { useGetProductsList } from '@scripts/hooks/useGetProductsList';
import { useGetUserData } from '@scripts/hooks/useGetUserData';
import { getBreadcrumbListJsonLd } from '@scripts/seo/getBreadcrumbListJsonLd';
import { getCommonProps } from '@scripts/server/getCommonProps';
import { ExtendedSSRBrandPageProps, ExtendedSSRDataProps } from '@scripts/server/types';

import styles from './index.module.scss';

const BrandPage = ({
    brandData: brandDataProps,
    isNotFound,
    isError = false,
    filters,
    seoData,
    dataProducts,
    totalSSR,
}: ExtendedSSRBrandPageProps) => {
    const productsListDataBack = dataProducts?.data || [];
    const [productDetailsData, setProductDetailsData] = useState<ProductDetail[]>(productsListDataBack || []);
    const lastProductsListDataRef = useRef<ProductDetail[] | null>(null);
    const lastProcessedQueryRef = useRef<string | null>(null);

    const { asPath, query } = useRouter();
    const pathname = getCorrectPathname(asPath);

    const [activeView, setActiveView] = useState<VIEW_OPTIONS>(VIEW_OPTIONS.DEFAULT);

    const totalPages = getTotalPages(dataProducts, ITEMS_PER_PRODUCTS_PAGE);

    const product = String(query ? query.brand : '').split('-');
    const id = product[product.length - 1];
    const activePage = +(query?.page || 1);
    const { data: { title = '', description = '', h1 = '' } = {} } = seoData || {};

    const { apiUser, error: userError, user } = useGetUserData();

    const brandData = brandDataProps?.data;

    const { preparedFilters, filtersListData: clientFiltersListData } = useCatalogFilters(
        { brand_id: id },
        {
            enabled: Boolean(id) && !user && !filters,
            enabledAuth: Boolean(id) && Boolean(apiUser) && !userError && !filters,
        }
    );

    const filtersListData = filters || clientFiltersListData;

    const [sortFilter, setSortFilter] = useState<SortFilter>(() => {
        const filters = safeParseFilters(query.filters);

        return {
            sort: readStoredCatalogSort(pathname, POPULAR_SORT_VALUE),
            isSale: filters.has_discount || false,
            isPreOrder: filters.isPreOrder === false,
        };
    });

    const { productsList: productsListData, total } = useGetProductsList(
        {
            sort: sortFilter.sort,
            filter: {
                brand_id: Number(id),
                ...preparedFilters,
                ...(sortFilter.isSale && { has_discount: sortFilter.isSale }),
                ...(sortFilter.isPreOrder && getInStockFilter()),
            },
            pagination: getPagination(ITEMS_PER_PRODUCTS_PAGE, activePage),
            include: ['brand', 'category', 'images', 'attributes', 'seller'],
        },
        {
            enabled: Boolean(id) && !user,
            enabledAuth: Boolean(id) && Boolean(apiUser),
            keepPreviousData: true,
        }
    );

    const breadcrumbsBrand = useMemo(
        () =>
            brandData
                ? [
                      { label: 'Главная', href: '/' },
                      { label: 'Бренды', href: '/brands' },
                      { label: h1 ? h1 : brandData.name, href: `/brands/${query.brand}` },
                  ]
                : null,
        [brandData, h1, query.brand]
    );

    const breadcrumbsJsonLd = useMemo(
        () => (breadcrumbsBrand ? getBreadcrumbListJsonLd(breadcrumbsBrand, getSiteOrigin()) : null),
        [breadcrumbsBrand]
    );

    const baseFilters = useMemo(() => {
        if (!id) return undefined;

        return { brand_id: Number(id) };
    }, [id]);

    const getQueryKey = () =>
        JSON.stringify({
            filters: query.filters,
            sort: query.sort,
            page: query.page,
        });

    const isLoadMore = query.isLoadMoreAvailable === 'true';

    useEffect(() => {
        const filters = safeParseFilters(query.filters);

        setSortFilter(prev => ({
            ...prev,
            isSale: filters.has_discount || false,
            isPreOrder: filters.isPreOrder === false,
        }));
    }, [query.filters]);

    useEffect(() => {
        if (!productsListData || productsListData === lastProductsListDataRef.current) {
            return;
        }

        if (productsListData.length === 0) {
            setProductDetailsData([]);
            lastProductsListDataRef.current = productsListData;
            lastProcessedQueryRef.current = getQueryKey();

            return;
        }

        lastProductsListDataRef.current = productsListData;
        lastProcessedQueryRef.current = getQueryKey();

        if (!isLoadMore) {
            setProductDetailsData([...productsListData]);
        } else {
            setProductDetailsData(prev => {
                const combined = [...prev, ...productsListData];

                return [...new Map(combined.map(item => [item.id, item])).values()];
            });
        }
    }, [productsListData, query.filters, query.sort, query.page, query.isLoadMoreAvailable]);

    useEffect(() => {
        if (productDetailsData.length > 0 && lastProcessedQueryRef.current === null) {
            lastProcessedQueryRef.current = getQueryKey();
        }
    }, [productDetailsData.length]);

    useEffect(() => {
        lastProductsListDataRef.current = null;
        lastProcessedQueryRef.current = null;

        return () => {
            setProductDetailsData([]);
        };
    }, [pathname]);

    return (
        <PageWrapper
            isError={isError}
            seoProps={{
                title: title ? title : `Купить товары бренда ${brandData?.name || ''} в интернет-магазине «Layta»`,
                description: description
                    ? description
                    : `Продажа товаров ${
                          brandData?.name || ''
                      } по выгодным ценам с доставкой в любой регион России в интернет-магазине «Layta».`,
                nameDigi: 'brand',
                contentDigi: 'Digi Brand Page',
            }}
            isNotFound={isNotFound}
        >
            <JsonLdScript data={breadcrumbsJsonLd} />
            <PageSection className={styles.page}>
                <div className={styles.stack}>
                    {breadcrumbsBrand && <Breadcrumbs items={breadcrumbsBrand} withPageOffset />}

                    <BrandInfo>
                        <div className={styles.brandInfoGrid}>
                            <div>
                                <div className={styles.mobileCount}>
                                    <BrandInfo.SubHeading>
                                        {total || totalSSR}{' '}
                                        {(!!total || !!totalSSR) &&
                                            declOfNum(total || totalSSR || 0, ['товар', 'товара', 'товаров'])}
                                    </BrandInfo.SubHeading>
                                </div>

                                <BrandInfo.LogoContainer>
                                    {brandData?.image_url ? (
                                        <Picture
                                            sources={[
                                                {
                                                    media: '',
                                                    image: transformImageUrl(brandData.image_url, 400, 160),
                                                },
                                            ]}
                                            loading="lazy"
                                            placeholder="empty"
                                            alt={h1 ? h1 : brandData.name}
                                            width={200}
                                            height={80}
                                            style={{
                                                objectFit: 'contain',
                                                width: 200,
                                                height: 80,
                                            }}
                                        />
                                    ) : (
                                        <div className={styles.logoFallback} />
                                    )}
                                </BrandInfo.LogoContainer>
                            </div>
                            <div>
                                <div className={styles.desktopHeading}>
                                    <BrandInfo.Heading>{h1 ? h1 : brandData?.name}</BrandInfo.Heading>

                                    {productDetailsData?.length !== 0 && (
                                        <BrandInfo.SubHeading>
                                            {total || totalSSR}{' '}
                                            {(!!total || !!totalSSR) &&
                                                declOfNum(total || totalSSR || 0, ['товар', 'товара', 'товаров'])}
                                        </BrandInfo.SubHeading>
                                    )}
                                </div>

                                {brandData?.description && (
                                    <BrandInfo.Description>{brandData.description}</BrandInfo.Description>
                                )}
                            </div>
                        </div>
                    </BrandInfo>

                    <hr className={styles.divider} />

                    <ProductsList
                        productsListData={
                            productDetailsData && productDetailsData.length > 0 ? productDetailsData : productsListData
                        }
                        totalPages={totalPages}
                        filters={filtersListData}
                        sortFilter={sortFilter}
                        setSortFilter={setSortFilter}
                        setProductDetailsData={setProductDetailsData}
                        activeView={activeView}
                        setActiveView={setActiveView}
                        baseFilters={baseFilters}
                        totalSSR={
                            productDetailsData && productDetailsData?.length > 0 ? productDetailsData?.length : total
                        }
                        isBrandPage={true}
                    />
                </div>
            </PageSection>
        </PageWrapper>
    );
};

export default BrandPage;

export const getServerSideProps = async (
    context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<ExtendedSSRDataProps>> =>
    getCommonProps(context, async (props, queryClient, apiClient) => {
        const { query } = context;

        const isAuthUser = !!props.accessToken;

        const brandId = context && context.query && context.query.brand && (context.query.brand as string).split('-');
        const id = brandId && brandId[brandId.length - 1];

        const urlFilters = { ...context.query };

        if (!id || Number.isNaN(Number(id))) {
            context.res.statusCode = HttpCode.NOT_FOUND;

            return { props: { ...props, isNotFound: true } };
        }

        if (urlFilters && Number(urlFilters.page) < 0) {
            context.res.statusCode = HttpCode.NOT_FOUND;

            return { props: { ...props, isNotFound: true } };
        }

        if ((query && Number(query.page) === 0) || (query && Number(query.page) === 1)) {
            return {
                redirect: {
                    destination: removeDefaultPaginationFromUrl(
                        '/brands' + (context.req.url as string).split('brands')[1]
                    ),
                    statusCode: 301,
                },
            };
        }

        try {
            const response = await apiClient.get(`catalog/brands/${id}`);

            const requestPath = String(context.query.brand || '');
            const brandPathCorrectly = `${response?.data.code}-${id}`;

            if (brandPathCorrectly !== requestPath) {
                context.res.statusCode = HttpCode.NOT_FOUND;

                return {
                    props: {
                        dehydratedState: dehydrate(queryClient),
                        isNotFound: true,
                        ...props,
                    },
                };
            }

            try {
                const { key: brandKey, fetch: brandFetch } = getBrand(apiClient)({
                    id: Number(id),
                });

                delete urlFilters.page;
                delete urlFilters.brand;
                const parsedFilters = safeParseFilters(urlFilters?.filters);

                const price_gte = parsedFilters?.price ? fromRoubleToKopecks(parsedFilters.price[0]) : null;
                const price_lte = parsedFilters?.price ? fromRoubleToKopecks(parsedFilters.price[1]) : null;

                const { price, isPreOrder, is_profitable, is_hit, ...restFilters } = parsedFilters;

                const preparedFilters = {
                    ...restFilters,
                    ...(price_gte && { price_gte }),
                    ...(price_lte && { price_lte }),
                    ...(isPreOrder === false && getInStockFilter()),
                    ...(is_profitable === true && { is_profitable: true }),
                    ...(is_hit === true && { is_hit: true }),
                };
                const { withoutLast: preparedFiltersWithoutLast, lastKey: lastFilterKey } =
                    splitLastFilter(preparedFilters);

                const { key: seoTagsKey, fetch: baseSeoTagsFetch } = getSeoTags(apiClient)(
                    getSeoFilter(
                        String(
                            `${`/brands/${context.query.brand}`}${
                                context.query.page ? `?page=${context.query.page}` : ''
                            }` || ''
                        ),
                        Number(id)
                    )
                );

                const seoData = await queryClient.fetchQuery({
                    queryKey: seoTagsKey,
                    queryFn: baseSeoTagsFetch,
                });

                const brandData = await queryClient.fetchQuery({
                    queryKey: brandKey,
                    queryFn: brandFetch,
                });

                const { key: baseProductsBrandKey, fetch: baseProductsBrandFetch } = getProducts(apiClient)({
                    sort: 'availability_order',
                    filter: {
                        brand_id: Number(id),
                        ...preparedFilters,
                    },
                    pagination: getPagination(ITEMS_PER_PRODUCTS_PAGE, Number(context.query.page)),
                    include: ['brand', 'category', 'images', 'attributes', 'seller'],
                });

                const { key: baseProductsAuthKey, fetch: baseProductsAuthFetch } = getProductsAuth(apiClient)({
                    sort: 'availability_order',
                    filter: {
                        brand_id: Number(id),
                        ...preparedFilters,
                    },
                    pagination: getPagination(ITEMS_PER_PRODUCTS_PAGE, Number(context.query.page)),
                    include: ['brand', 'category', 'images', 'attributes', 'seller'],
                });

                const dataProducts = await queryClient.fetchQuery({
                    queryKey: isAuthUser ? baseProductsAuthKey : baseProductsBrandKey,
                    queryFn: isAuthUser ? baseProductsAuthFetch : baseProductsBrandFetch,
                });

                const { key: filtersKey, fetch: filtersFetch } = getProductFilters(apiClient)({
                    filter: {
                        brand_id: Number(id),
                        ...preparedFilters,
                    },
                });

                const { key: filtersLimitedKey, fetch: filtersLimitedFetch } = getProductFilters(apiClient)({
                    filter: {
                        brand_id: Number(id),
                        ...preparedFiltersWithoutLast,
                    },
                });

                const { key: filtersBaseKey, fetch: filtersBaseFetch } = getProductFilters(apiClient)({
                    filter: {
                        brand_id: Number(id),
                    },
                });

                const [filtersResponse, filtersLimitedResponse, filtersBaseResponse] = await Promise.all([
                    queryClient.fetchQuery({
                        queryKey: filtersKey,
                        queryFn: filtersFetch,
                    }),
                    queryClient.fetchQuery({
                        queryKey: filtersLimitedKey,
                        queryFn: filtersLimitedFetch,
                    }),
                    queryClient.fetchQuery({
                        queryKey: filtersBaseKey,
                        queryFn: filtersBaseFetch,
                    }),
                ]);

                const filters = mergeProductsFilterResponses(
                    filtersBaseResponse.data,
                    filtersResponse.data,
                    filtersLimitedResponse.data,
                    lastFilterKey ? String(lastFilterKey) : undefined
                );

                const paginationPage = dataProducts?.meta?.pagination;

                if (
                    paginationPage &&
                    query.page &&
                    +query.page > Math.ceil(+paginationPage.total / +paginationPage.limit)
                ) {
                    context.res.statusCode = HttpCode.NOT_FOUND;

                    return { props: { ...props, isNotFound: true } };
                }

                return {
                    props: {
                        id: id,
                        dehydratedState: dehydrate(queryClient),
                        seoData: seoData,
                        brandData: brandData,
                        filters: filters,
                        dataProducts: dataProducts,
                        totalSSR: dataProducts?.meta?.pagination?.total || 0,
                        ...props,
                    },
                };
            } catch {
                // Brand exists and slug is valid — secondary API failures are real server errors.
                context.res.statusCode = HttpCode.INTERNAL_SERVER_ERROR;

                return {
                    props: {
                        ...props,
                        isError: true,
                    },
                };
            }
        } catch (error: unknown) {
            const status =
                error instanceof FetchError
                    ? error.status
                    : typeof error === 'object' && error && 'status' in error
                      ? Number((error as { status?: number }).status)
                      : undefined;

            if (status === HttpCode.NOT_FOUND) {
                context.res.statusCode = HttpCode.NOT_FOUND;

                return {
                    props: {
                        dehydratedState: dehydrate(queryClient),
                        isNotFound: true,
                        ...props,
                    },
                };
            }

            context.res.statusCode = HttpCode.INTERNAL_SERVER_ERROR;

            return {
                props: {
                    ...props,
                    isError: true,
                },
            };
        }
    });
